import { useEffect, useMemo, useState } from "react";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from "../components/ui/table";
import {
  adminApi,
  type NativeAgentRow,
  type CustomAgentRow,
} from "../services/adminApi";
import { toast } from "sonner";

type TabId = "native" | "custom";

const INHERIT_MODEL_VALUE = "__inherit_org_default__";

interface ProductModel {
  id: string;
  name: string;
  description: string | null;
  modelName: string;
  provider?: { id: string; name: string };
  displayOrder: number;
  isActive: boolean;
  isDefault: boolean;
  tier: number;
}

function productModelOptionLabel(model: ProductModel) {
  const tags = [];
  if (model.isDefault) tags.push("default");
  if (!model.isActive) tags.push("inactive");
  return tags.length > 0 ? `${model.name} (${tags.join(", ")})` : model.name;
}

function productModelDescription(
  model: ProductModel | undefined,
  productModelId: string | null,
) {
  if (!productModelId) return "Inherits org/default model";
  if (!model) return `Missing model: ${productModelId}`;
  const provider = model.provider?.name ? `${model.provider.name} / ` : "";
  return `${provider}${model.modelName}${model.isActive ? "" : " · inactive"}`;
}

export default function NativeAgentsPage() {
  const [activeTab, setActiveTab] = useState<TabId>("native");

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold text-gray-900">Agent Management</h1>
      </div>

      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-6" aria-label="Tabs">
          {(["native", "custom"] as TabId[]).map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`whitespace-nowrap border-b-2 px-1 py-2 text-sm font-medium ${
                activeTab === tab
                  ? "border-blue-600 text-blue-600"
                  : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
              }`}
            >
              {tab === "native" ? "Native Agents" : "Custom Agents"}
            </button>
          ))}
        </nav>
      </div>

      {activeTab === "native" ? <NativeTab /> : <CustomTab />}
    </div>
  );
}

function NativeTab() {
  const [agents, setAgents] = useState<NativeAgentRow[]>([]);
  const [productModels, setProductModels] = useState<ProductModel[]>([]);
  const [loading, setLoading] = useState(true);
  const [reseeding, setReseeding] = useState(false);
  const [togglingSlug, setTogglingSlug] = useState<string | null>(null);
  const [savingModelSlug, setSavingModelSlug] = useState<string | null>(null);

  useEffect(() => {
    void fetchAgents();
  }, []);

  const fetchAgents = async () => {
    try {
      setLoading(true);
      const [rows, models] = await Promise.all([
        adminApi.getNativeAgents(),
        adminApi.getProductModels(),
      ]);
      setAgents(rows);
      setProductModels(models);
    } catch (e: any) {
      toast.error(e?.response?.data?.error ?? "Failed to load native agents");
    } finally {
      setLoading(false);
    }
  };

  const handleReseed = async () => {
    if (
      !confirm(
        "Re-seed all native agents? This is safe and idempotent. Use this to push seed-owned fields for built-in agents like Conductor and Daily Brief.",
      )
    )
      return;
    try {
      setReseeding(true);
      const result = await adminApi.reseedNativeAgents();
      toast.success(
        `Seeded ${result.count} native agents: ${result.seeded.join(", ")}`,
      );
      await fetchAgents();
    } catch (e: any) {
      toast.error(e?.response?.data?.error ?? "Re-seed failed");
    } finally {
      setReseeding(false);
    }
  };

  const handleToggleActive = async (agent: NativeAgentRow) => {
    try {
      setTogglingSlug(agent.slug);
      const updated = await adminApi.patchNativeAgent(agent.slug, {
        isActive: !agent.isActive,
      });
      setAgents((prev) =>
        prev.map((a) => (a.slug === agent.slug ? updated : a)),
      );
      toast.success(
        `${updated.name} ${updated.isActive ? "enabled" : "disabled"}`,
      );
    } catch (e: any) {
      toast.error(e?.response?.data?.error ?? "Update failed");
    } finally {
      setTogglingSlug(null);
    }
  };

  const handleModelChange = async (agent: NativeAgentRow, value: string) => {
    const productModelId = value === INHERIT_MODEL_VALUE ? null : value;
    if (productModelId === agent.productModelId) return;
    try {
      setSavingModelSlug(agent.slug);
      const updated = await adminApi.patchNativeAgent(agent.slug, {
        productModelId,
      });
      setAgents((prev) =>
        prev.map((a) => (a.slug === agent.slug ? updated : a)),
      );
      toast.success(`${updated.name} model updated`);
    } catch (e: any) {
      toast.error(e?.response?.data?.error ?? "Model update failed");
    } finally {
      setSavingModelSlug(null);
    }
  };

  const productModelById = useMemo(() => {
    const map = new Map<string, ProductModel>();
    for (const model of productModels) map.set(model.id, model);
    return map;
  }, [productModels]);

  const sortedProductModels = useMemo(
    () =>
      [...productModels].sort(
        (a, b) =>
          a.displayOrder - b.displayOrder || a.name.localeCompare(b.name),
      ),
    [productModels],
  );

  const internalCount = agents.filter((agent) => agent.isInternal).length;
  const activeCount = agents.filter((agent) => agent.isActive).length;

  if (loading) {
    return <div className="text-sm text-gray-500">Loading...</div>;
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-gray-600">
            Native agents are seeded from code. Internal agents are
            built-in/read-only in Library; non-internal agents stay copyable
            templates until a user imports them.
          </p>
          <p className="mt-1 text-xs text-gray-500">
            {agents.length} native agents · {internalCount} built-in ·{" "}
            {activeCount} active
          </p>
        </div>
        <Button
          onClick={handleReseed}
          disabled={reseeding}
          className="shrink-0"
        >
          {reseeding ? "Seeding..." : "Re-seed native agents"}
        </Button>
      </div>

      <Card className="shadow-sm border-0 ring-1 ring-gray-200">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Slug</TableHead>
                <TableHead>Persona</TableHead>
                <TableHead>Kind</TableHead>
                <TableHead>Model</TableHead>
                <TableHead className="text-right">Active</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {agents.map((agent) => {
                const selectedModel = agent.productModelId
                  ? productModelById.get(agent.productModelId)
                  : undefined;
                const missingSelectedModel =
                  agent.productModelId !== null && selectedModel === undefined;

                return (
                  <TableRow key={agent.slug}>
                    <TableCell className="font-medium">{agent.name}</TableCell>
                    <TableCell className="font-mono text-xs text-gray-500">
                      {agent.slug}
                    </TableCell>
                    <TableCell className="text-sm text-gray-600">
                      {agent.personaName ?? "—"}
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1.5">
                        {agent.isInternal ? (
                          <Badge className="bg-gray-100 text-gray-600 text-xs">
                            built-in
                          </Badge>
                        ) : (
                          <Badge className="bg-green-100 text-green-700 text-xs">
                            library
                          </Badge>
                        )}
                        {agent.canSpawnAgents && (
                          <Badge className="bg-blue-100 text-blue-800 text-xs">
                            can spawn
                          </Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="min-w-[280px]">
                      <select
                        value={agent.productModelId ?? INHERIT_MODEL_VALUE}
                        disabled={savingModelSlug === agent.slug || reseeding}
                        onChange={(e) =>
                          void handleModelChange(agent, e.target.value)
                        }
                        className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:bg-gray-100 disabled:text-gray-500"
                      >
                        <option value={INHERIT_MODEL_VALUE}>
                          Inherit org/default model
                        </option>
                        {missingSelectedModel && (
                          <option value={agent.productModelId!}>
                            Missing model ({agent.productModelId})
                          </option>
                        )}
                        {sortedProductModels.map((model) => (
                          <option key={model.id} value={model.id}>
                            {productModelOptionLabel(model)}
                          </option>
                        ))}
                      </select>
                      <div className="mt-1 text-xs text-gray-500">
                        {savingModelSlug === agent.slug
                          ? "Saving model..."
                          : productModelDescription(
                              selectedModel,
                              agent.productModelId,
                            )}
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      <label className="inline-flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={agent.isActive}
                          disabled={togglingSlug === agent.slug || reseeding}
                          onChange={() => void handleToggleActive(agent)}
                          className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
                        />
                        <span className="text-sm text-gray-600">
                          {agent.isActive ? "On" : "Off"}
                        </span>
                      </label>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}

function CustomTab() {
  const [agents, setAgents] = useState<CustomAgentRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const PAGE_SIZE = 50;

  useEffect(() => {
    void fetchAgents(page);
  }, [page]);

  const fetchAgents = async (p: number) => {
    try {
      setLoading(true);
      const result = await adminApi.getCustomAgents(p, PAGE_SIZE);
      setAgents(result.agents);
      setTotalPages(result.pagination.pages);
      setTotal(result.pagination.total);
    } catch (e: any) {
      toast.error(e?.response?.data?.error ?? "Failed to load custom agents");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-gray-600">
          {total} custom agents across all organizations (read-only)
        </p>
      </div>

      <Card className="shadow-sm border-0 ring-1 ring-gray-200">
        <CardContent className="p-0">
          {loading ? (
            <div className="p-6 text-sm text-gray-500">Loading...</div>
          ) : agents.length === 0 ? (
            <div className="p-6 text-sm text-gray-500">
              No custom agents found.
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Organization</TableHead>
                  <TableHead>Model</TableHead>
                  <TableHead>Active</TableHead>
                  <TableHead>Created</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {agents.map((agent) => (
                  <TableRow key={agent.id}>
                    <TableCell className="font-medium">{agent.name}</TableCell>
                    <TableCell className="text-sm text-gray-600">
                      {agent.organization?.name ?? (
                        <span className="text-gray-400 italic">unknown</span>
                      )}
                    </TableCell>
                    <TableCell className="text-xs text-gray-500">
                      {agent.productModelId ?? (
                        <span className="italic text-gray-400">default</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge
                        className={
                          agent.isActive
                            ? "bg-green-100 text-green-700 text-xs"
                            : "bg-gray-100 text-gray-500 text-xs"
                        }
                      >
                        {agent.isActive ? "Active" : "Inactive"}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-xs text-gray-500">
                      {new Date(agent.createdAt).toLocaleDateString()}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {totalPages > 1 && (
        <div className="flex items-center justify-between text-sm text-gray-600">
          <span>
            Page {page} of {totalPages}
          </span>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={page <= 1}
              onClick={() => setPage((p) => p - 1)}
            >
              Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={page >= totalPages}
              onClick={() => setPage((p) => p + 1)}
            >
              Next
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
